<template>
  <div>
    <div class="flex items-center gap-1 flex-nowrap">
      <button
        v-if="hasChildren"
        :id="`toggle-asset-children-${asset.id}`"
        class="outline-none text-xs text-[#68737D]"
        type="button"
        @click="$emit('toggleChildren', asset.id)"
      >
        <ChevronDownLarge :custom-style="`size-3.5 ${!asset.expandedChildren ? 'rotate-[270deg]' : ''}`" />
      </button>
      <!-- Hidden opacity in order to trick assets with no children to stay on the same flex position as their siblings -->
      <button
        v-else
        class="opacity-0"
        type="button"
      >
        <ChevronDownLarge custom-style="size-3.5" />
      </button>
      <div
        class="flex items-center gap-2 pr-4 flex-nowrap"
        :class="{ 'cursor-not-allowed opacity-30': asset.disabledSelection }"
      >
        <input
          :id="`asset-checkbox-${asset.id}`"
          v-model="asset.checked"
          type="checkbox"
          class="p-1.5 bg-white border-2 border-gray-400 rounded-sm cursor-pointer hover:border-gray-500 text-main-primary focus:ring-transparent focus:border-main-primary"
          :disabled="asset.disabledSelection"
          @change="$emit('toggleCheckAsset', { checked: $event.target.checked, assetId: asset.id })"
        >
        <span class="text-sm whitespace-nowrap no-localize-translate">{{ asset.asset_name }}</span>
        <span
          v-if="classTitle"
          :id="`asset-class-badge-${asset.id}`"
          class="whitespace-nowrap text-xs px-1.5 py-0.5 rounded"
          :class="classColors"
        >{{ classTitle }}</span>
      </div>
    </div>
    <!-- Children -->
    <Transition
      enter-class="opacity-0"
      enter-active-class="transition-all duration-300"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-active-class="transition-all duration-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="canViewChildren"
        class="flex flex-col pl-4 mt-2 gap-y-2"
      >
        <AssetTreeNode
          v-for="child in asset.children"
          :key="child.id"
          :node="child"
          :classes="classes"
          @toggle-children="$emit('toggleChildren', $event)"
          @toggle-check-asset="$emit('toggleCheckAsset', $event)"
        />
      </div>
    </Transition>
  </div>
</template>

<script>
export default {
  name: 'AssetTreeNode',

  props: {
    node: {
      type: Object,
      required: true,
    },
    classes: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  emits: ['toggleCheckAsset', 'toggleChildren'],

  computed: {
    canViewChildren() {
      return this.hasChildren && this.asset.expandedChildren
    },
    hasChildren() {
      return !!this.asset.children.length
    },
    asset() {
      return this.node
    },
    classTitle() {
      const cls = this.classes.find(
        ({ asset_class_id }) =>
          asset_class_id === this.asset.asset_classification.id,
      )
      return cls?.asset_class_title ?? ''
    },
    classColors() {
      const cls = this.classes.find(
        ({ asset_class_id }) =>
          asset_class_id === this.asset.asset_classification.id,
      )
      const color = cls?.randomColor
      let colors = ''

      switch (color) {
        case 'red':
          colors = 'bg-red-50 text-red-500'
          break
        case 'orange':
          colors = 'bg-orange-50 text-orange-500'
          break
        case 'yellow':
          colors = 'bg-yellow-50 text-yellow-500'
          break
        case 'green':
          colors = 'bg-green-50 text-green-500'
          break
        case 'blue':
          colors = 'bg-blue-50 text-blue-500'
          break
        case 'purple':
          colors = 'bg-purple-50 text-purple-500'
          break
        case 'rose':
          colors = 'bg-rose-50 text-rose-500'
          break
        case 'stone':
          colors = 'bg-stone-50 text-stone-500'
          break
        case 'amber':
          colors = 'bg-amber-50 text-amber-500'
          break
        case 'lime':
          colors = 'bg-lime-50 text-lime-500'
          break
        case 'teal':
          colors = 'bg-teal-50 text-teal-500'
          break
        case 'fuchsia':
          colors = 'bg-fuchsia-50 text-fuchsia-500'
          break
      }

      return colors
    },
  },
}
</script>
